import { Button, message, Spin, Switch } from 'antd';
import React from 'react';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import AvaIcon from 'assets/common/ava.png';
import { EAvaTone, EAIChatPageId } from 'types/ai';
import AIChat from 'components/AIChat';
import AppointmentLinkEdit from './components/AppointmentLinkEdit';
import NotificationEmailEdit from './components/NotificationEmailEdit';
import useProspectSettingInfo from 'hooks/useProspectSettingInfo';
import { saveProspectSetting } from 'api/prospect';
import { setShowAvaSettingInProsepect } from 'utils/localstore';
import { isFreeUser, shouldShowKlarityPlanUI } from 'utils/provider';
import ProviderStore from 'store/Provider';
import SubscriptionStore from 'store/Subscription';
import { getUrlParam, shouldUseKlarityUi } from 'utils/common';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import GreetingSetUpModal from '../InboxPage/GreetingSetUpModal';
import { saveGreetingMessage } from 'api/common';

const AvaSettingPage = () => {
    const navigate = useNavigate();
    const [getUser] = ProviderStore.useStore();
    const [getSubscription] = SubscriptionStore.useStore();
    const user = getUser('data');
    const loadingUser = getUser('loading');
    const loadingPlan = getSubscription('loading');
    const hasPlan = !isFreeUser();
    const fromInbox = getUrlParam('from') === 'inbox';
    const [data, loadingHome, fetchData] = useProviderHomeInfo();
    const [showGreetingSetUpModal, setShowGreetingSetUpModal] = React.useState(false);

    const [isOpenAva, setIsOpenAva] = React.useState(false);
    const [avaTony, setAvaTony] = React.useState<EAvaTone>();
    const [isAutoGreeting, setIsAutoGreeting] = React.useState(false);

    const [hasLoaded, setHasLoaded] = React.useState(false);

    const allowCustomLink = !!(data && !data.ppaProvider);

    const avaTonyOption = [
        {
            dictKey: EAvaTone.FRIENDLY,
            content: EAvaTone.FRIENDLY,
        },
        {
            dictKey: EAvaTone.CASUAL,
            content: EAvaTone.CASUAL,
        },
        {
            dictKey: EAvaTone.EMPATHETIC,
            content: EAvaTone.EMPATHETIC,
        },
        {
            dictKey: EAvaTone.FORMAL,
            content: EAvaTone.FORMAL,
        },
    ];
    const [info, loadingInfo, fetchInfo] = useProspectSettingInfo();
    const [switching, setSwitching] = React.useState(false);
    const [switchingGreet, setSwitchingGreet] = React.useState(false);
    const [changingTony, setChangingTony] = React.useState(false);
    const shouldShowKlarityUi = shouldUseKlarityUi(data);

    React.useEffect(() => {
        if (info) {
            setIsOpenAva(info.avaAutoReply);
            setAvaTony(info.avaTone);
            setShowAvaSettingInProsepect('true');
            setHasLoaded(true);
            setIsAutoGreeting(!info.greeting || info.greeting?.status === 'A');
        }
    }, [info]);

    const handleChangeAvaSwitch = async (check: boolean) => {
        const current = isOpenAva;
        setIsOpenAva(check);
        setSwitching(true);
        const result = await saveProspectSetting(null, check);
        if (!result.error) {
            message.success('Save successfully');
        } else {
            //roll back
            setIsOpenAva(current);
        }
        setSwitching(false);
    };

    const handleChangeGreetingSwitch = async (check: boolean) => {
        const current = isAutoGreeting;
        setIsAutoGreeting(check);
        setSwitchingGreet(true);
        const status = check ? 'A' : 'D';
        const result = await saveGreetingMessage({
            messageTone: info?.greeting?.messageTone || '',
            status,
            isAiAssisted: info?.greeting?.isAiAssisted || false,
            customPrompt: info?.greeting?.customPrompt || '',
            greetingMessage: info?.greeting?.greetingMessage || '',
        });
        if (!result.error) {
            message.success('Save successfully');
        } else {
            //roll back
            setIsAutoGreeting(current);
        }
        setSwitchingGreet(false);
    };

    const handleAvaTonyChange = async (tonyName: string) => {
        if (!tonyName) {
            return;
        }
        const current = avaTony;
        setAvaTony(tonyName as EAvaTone);
        setChangingTony(true);
        const result = await saveProspectSetting(tonyName, null);
        if (!result.error) {
            message.success('Save successfully');
        } else {
            //roll back
            setAvaTony(tonyName as EAvaTone);
        }
        setChangingTony(false);
    };

    // 将文本中的占位符转换为带标签的 HTML
    const renderContentWithLabels = (text: string): string => {
        // 匹配所有 [Placeholder] 格式的占位符
        const placeholderRegex = /\[([^\]]+)\]/g;
        return text.replace(placeholderRegex, (match, placeholderName) => {
            // 添加 contentEditable="false" 使 label 不可编辑
            return `
                <span class="${s.label}" data-placeholder="${match}" contenteditable="false">${placeholderName} 
                <span class="${s.close}"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M4 12L12 4M4 4L12 12" stroke="#111827" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
    </svg></span></span>`;
        });
    };

    const handleEditInstruction = () => {
        setShowGreetingSetUpModal(true);
    };

    const hasConfigGreeting = info?.greeting?.greetingMessage || info?.greeting?.customPrompt;

    if (!info || !hasLoaded) {
        return (
            <div className={s.loadingWrap}>
                <Spin />
            </div>
        );
    }

    return (
        <div className={s.wrap}>
            <div
                onClick={() => {
                    if (fromInbox) {
                        navigate(`${PATH.DASHBOARD}/${PATH.INBOX}`);
                        return;
                    }
                    navigate(`${PATH.DASHBOARD}/${PATH.PROSPECTS}`);
                }}
                className={s.nav}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.70711 16.7071C9.31659 17.0976 8.68342 17.0976 8.2929 16.7071L2.29289 10.7071C1.90237 10.3166 1.90237 9.68342 2.29289 9.29289L8.2929 3.29289C8.68342 2.90237 9.31659 2.90237 9.70712 3.29289C10.0976 3.68342 10.0976 4.31658 9.70712 4.70711L5.41422 9H17C17.5523 9 18 9.44772 18 10C18 10.5523 17.5523 11 17 11L5.41422 11L9.70711 15.2929C10.0976 15.6834 10.0976 16.3166 9.70711 16.7071Z"
                        fill="currentColor"
                    />
                </svg>
                {fromInbox ? 'Back to message' : 'Back to clients'}
            </div>
            <div className={s.header}>
                <h3 className={s.title}>Settings</h3>
            </div>
            {/* {
                !hasPlan && (
                    <div className={s.bannerWrap}>
                        <KiwiUpgradeBanner />
                    </div>
                )
            } */}
            <Spin spinning={loadingInfo}>
                <div className={s.content}>
                    <div className={s.module}>
                        <div className={`${s.greetTitle} ${!hasConfigGreeting ? s.greetTitleNoConfig : ''}`}>
                            <div className={s.mTitle}>
                                <div className={s.left}>Automated new patient greetings</div>
                                <div className={s.right}>
                                    {hasConfigGreeting && (
                                        <Switch
                                            loading={switchingGreet}
                                            checked={isAutoGreeting}
                                            onChange={handleChangeGreetingSwitch}
                                        />
                                    )}
                                    {!hasConfigGreeting && (
                                        <Button type="primary" onClick={handleEditInstruction}>
                                            Set up now
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className={s.subTitle}>
                                Send an automated greeting message to every new patient after their first visit is
                                booked.
                            </div>
                        </div>
                        {hasConfigGreeting && (
                            <div className={s.greetContent}>
                                <div className={s.cHeader}>
                                    <span>Instruction</span>
                                    <span onClick={handleEditInstruction} className={s.edit}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M20.2677 3.73223L20.9748 3.02513V3.02513L20.2677 3.73223ZM6.5 21.0355V22.0355C6.76522 22.0355 7.01957 21.9301 7.20711 21.7426L6.5 21.0355ZM3 21.0355H2C2 21.5878 2.44772 22.0355 3 22.0355V21.0355ZM3 17.4644L2.29289 16.7573C2.10536 16.9448 2 17.1992 2 17.4644H3ZM16.7322 3.73223L17.4393 4.43934C18.0251 3.85355 18.9748 3.85355 19.5606 4.43934L20.2677 3.73223L20.9748 3.02513C19.608 1.65829 17.3919 1.65829 16.0251 3.02513L16.7322 3.73223ZM20.2677 3.73223L19.5606 4.43934C20.1464 5.02513 20.1464 5.97487 19.5606 6.56066L20.2677 7.26777L20.9748 7.97487C22.3417 6.60804 22.3417 4.39196 20.9748 3.02513L20.2677 3.73223ZM20.2677 7.26777L19.5606 6.56066L5.79289 20.3284L6.5 21.0355L7.20711 21.7426L20.9748 7.97487L20.2677 7.26777ZM6.5 21.0355V20.0355H3V21.0355V22.0355H6.5V21.0355ZM16.7322 3.73223L16.0251 3.02513L2.29289 16.7573L3 17.4644L3.70711 18.1715L17.4393 4.43934L16.7322 3.73223ZM3 17.4644H2V21.0355H3H4V17.4644H3ZM15.2322 5.23223L14.5251 5.93934L18.0606 9.47487L18.7677 8.76777L19.4748 8.06066L15.9393 4.52513L15.2322 5.23223Z"
                                                fill="#111827"
                                            />
                                        </svg>
                                    </span>
                                </div>

                                <div className={s.cContent}>
                                    {info?.greeting?.isAiAssisted ? (
                                        <div
                                            className={s.cContentText}
                                            dangerouslySetInnerHTML={{
                                                __html: renderContentWithLabels(info?.greeting?.customPrompt || ''),
                                            }}
                                        />
                                    ) : (
                                        <div
                                            className={s.cContentText}
                                            dangerouslySetInnerHTML={{
                                                __html: renderContentWithLabels(info?.greeting?.greetingMessage || ''),
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                        {!hasConfigGreeting && (
                            <div className={s.greetingNote}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                >
                                    <path
                                        d="M9 1C9 0.447715 8.55229 0 8 0C7.44771 0 7 0.447715 7 1V2C7 2.55228 7.44771 3 8 3C8.55229 3 9 2.55228 9 2V1Z"
                                        fill="#60A5FA"
                                    />
                                    <path
                                        d="M13.6568 3.75731C14.0473 3.36678 14.0473 2.73362 13.6568 2.34309C13.2663 1.95257 12.6331 1.95257 12.2426 2.34309L11.5355 3.0502C11.145 3.44072 11.145 4.07389 11.5355 4.46441C11.926 4.85494 12.5592 4.85494 12.9497 4.46441L13.6568 3.75731Z"
                                        fill="#60A5FA"
                                    />
                                    <path
                                        d="M16 8C16 8.55229 15.5523 9 15 9H14C13.4477 9 13 8.55228 13 8C13 7.44771 13.4477 7 14 7H15C15.5523 7 16 7.44771 16 8Z"
                                        fill="#60A5FA"
                                    />
                                    <path
                                        d="M3.05019 4.46443C3.44071 4.85496 4.07388 4.85496 4.4644 4.46443C4.85493 4.07391 4.85493 3.44074 4.4644 3.05022L3.7573 2.34311C3.36677 1.95259 2.73361 1.95259 2.34308 2.34311C1.95256 2.73363 1.95256 3.3668 2.34308 3.75732L3.05019 4.46443Z"
                                        fill="#60A5FA"
                                    />
                                    <path
                                        d="M3 8C3 8.55229 2.55228 9 2 9H1C0.447715 9 -1.19209e-07 8.55228 0 8C0 7.44771 0.447715 7 1 7H2C2.55228 7 3 7.44771 3 8Z"
                                        fill="#60A5FA"
                                    />
                                    <path
                                        d="M6 14V13H10V14C10 15.1046 9.10457 16 8 16C6.89543 16 6 15.1046 6 14Z"
                                        fill="#60A5FA"
                                    />
                                    <path
                                        d="M10.0009 12C10.0155 11.6597 10.2076 11.3537 10.4768 11.1411C11.4046 10.4086 12 9.27384 12 8C12 5.79086 10.2091 4 8 4C5.79086 4 4 5.79086 4 8C4 9.27384 4.59545 10.4086 5.52319 11.1411C5.79241 11.3537 5.98451 11.6597 5.99911 12H10.0009Z"
                                        fill="#60A5FA"
                                    />
                                </svg>
                                Providers who set up greetings see <b>27% more patient engagement</b> before the first
                                visit.
                            </div>
                        )}
                    </div>

                    <div className={s.module}>
                        <div className={s.mTitle}>
                            <div className={s.left}>Auto-reply</div>
                            <div className={s.right}>
                                {isOpenAva && (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="8"
                                            height="8"
                                            viewBox="0 0 8 8"
                                            fill="none"
                                        >
                                            <circle cx="4" cy="4" r="3" fill="#00816B" />
                                        </svg>
                                        <img src={AvaIcon} className={s.avaIcon} alt="ava" />
                                        <span className={s.textOn}>auto-reply on</span>
                                    </>
                                )}
                                {!isOpenAva && (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="8"
                                            height="8"
                                            viewBox="0 0 8 8"
                                            fill="none"
                                        >
                                            <circle cx="4" cy="4" r="3" fill="#6B7280" />
                                        </svg>
                                        <span className={s.avaText}>Ava</span>
                                        <span className={s.textOff}>auto-reply on</span>
                                    </>
                                )}
                                <Switch
                                    disabled={!hasPlan && !data?.ppaProvider}
                                    defaultChecked={isOpenAva}
                                    loading={switching}
                                    checked={isOpenAva}
                                    onChange={handleChangeAvaSwitch}
                                />
                            </div>
                        </div>
                        <div className={s.switchContent}>
                            <div className={s.des}>
                                By enabling auto-reply, Ava will instantly respond to prospect inquiries using
                                information from your UniProfile and appointment settings. This will save you time while
                                ensuring accurate, and personalized replies that clearly communicate your requirements.
                            </div>
                            <div className={s.des}>
                                With auto-reply off, Ava will seamlessly switch to Co-pilot mode. She'll draft responses
                                for you to review and send it to prospects, giving you full control over the
                                conversation.
                            </div>
                        </div>
                    </div>

                    <div className={s.module}>
                        <div className={s.mTitle}>
                            <div className={s.left}>Set Ava's tone</div>
                        </div>
                        <div className={s.des} style={{ marginTop: '24px' }}>
                            Ava will generally use the tone you selected, but may adapt her responses to match the
                            context of each message.
                        </div>
                        <div className={s.tonyContent}>
                            <div className={s.left}>
                                <Spin spinning={changingTony}>
                                    {avaTonyOption.map((item) => {
                                        return (
                                            <div
                                                onClick={() => {
                                                    if (changingTony) {
                                                        return;
                                                    }
                                                    handleAvaTonyChange(item.content);
                                                }}
                                                className={`${s.tonyItem} ${avaTony === item.content ? s.tonyItemActive : ''}`}
                                            >
                                                {item.content}
                                            </div>
                                        );
                                    })}
                                </Spin>
                            </div>
                            <div className={s.right}>
                                <div className={s.p0}>Ava will reply with a {avaTony} tone like:</div>
                                <div className={s.p1}>
                                    <div className={s.p1Icon}>P</div>
                                    <div className={s.p1Content}>
                                        Hello, I'm reaching out to inquire if you are currently accepting new patients
                                        and if you have experience treating anxiety.
                                    </div>
                                </div>
                                <div className={s.p2}>
                                    <div className={s.p2Content}>
                                        {avaTony === EAvaTone.FRIENDLY && (
                                            <div className={s.text}>
                                                Thank you for reaching out, Elisabeth. I'm currently accepting new
                                                patients and would be happy to assist with your anxiety treatment. To
                                                schedule an appointment, please visit my appointment link:{' '}
                                                <a>bookinglink.com/provider</a>. If you have any further questions, you
                                                can contact me via 123-555-6688.
                                            </div>
                                        )}
                                        {avaTony === EAvaTone.CASUAL && (
                                            <div className={s.text}>
                                                Hey Elisabeth! Thanks for getting in touch. I am accepting new patients
                                                and would be happy to help you with your anxiety. Feel free to book an
                                                appointment online at <a>bookinglink.com/provider</a> or give me a call
                                                at 123-555-6688 if you have any questions.
                                            </div>
                                        )}
                                        {avaTony === EAvaTone.EMPATHETIC && (
                                            <div className={s.text}>
                                                Hello Elisabeth, I understand reaching out about anxiety can be a big
                                                step, so thank you for contacting me. I am currently accepting new
                                                patients and specialize in anxiety treatment. Please feel free to
                                                schedule an appointment at your convenience at{' '}
                                                <a>bookinglink.com/provider</a> or call me at 123-555-6688 if you'd like
                                                to discuss anything further.
                                            </div>
                                        )}
                                        {avaTony === EAvaTone.FORMAL && (
                                            <div className={s.text}>
                                                Dear Ms. Elisabeth,
                                                <br />
                                                Thank you for your inquiry. I am pleased to confirm that I am currently
                                                accepting new patients and have extensive experience in treating
                                                anxiety. Please visit <a>bookinglink.com/provider</a> to schedule an
                                                appointment, or contact my office directly at 123-555-6688 if you
                                                require additional information.
                                            </div>
                                        )}
                                        <div className={s.name}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="13"
                                                height="13"
                                                viewBox="0 0 13 13"
                                                fill="none"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M6.30337 11.2923C8.92923 11.2923 11.0579 9.1636 11.0579 6.53774C11.0579 3.91188 8.92923 1.7832 6.30337 1.7832C3.67751 1.7832 1.54883 3.91188 1.54883 6.53774C1.54883 9.1636 3.67751 11.2923 6.30337 11.2923ZM8.50656 5.76935C8.73866 5.53726 8.73866 5.16096 8.50656 4.92886C8.27447 4.69677 7.89817 4.69677 7.66607 4.92886L5.70905 6.88588L4.94066 6.1175C4.70856 5.8854 4.33226 5.8854 4.10017 6.1175C3.86807 6.34959 3.86807 6.72589 4.10017 6.95799L5.2888 8.14662C5.5209 8.37872 5.8972 8.37872 6.12929 8.14662L8.50656 5.76935Z"
                                                    fill="#069D70"
                                                />
                                            </svg>
                                            Replied by
                                            <img src={AvaIcon} className={s.avaIconName} alt="ava" />
                                        </div>
                                    </div>
                                    <div className={s.p2Icon}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="34"
                                            height="34"
                                            viewBox="0 0 34 34"
                                            fill="none"
                                        >
                                            <path
                                                d="M17 33C25.8365 33 33 25.8365 33 17C33 8.16346 25.8365 1 17 1C8.16346 1 1 8.16346 1 17C1 25.8365 8.16346 33 17 33Z"
                                                fill="#F0FCF6"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M0.273438 16.9997C0.273438 7.76153 7.76251 0.272461 17.0007 0.272461C26.2389 0.272461 33.728 7.76153 33.728 16.9997C33.728 26.2379 26.2389 33.727 17.0007 33.727C7.76251 33.727 0.273438 26.2379 0.273438 16.9997ZM17.0007 1.72701C8.56583 1.72701 1.72798 8.56485 1.72798 16.9997C1.72798 25.4346 8.56583 32.2725 17.0007 32.2725C25.4356 32.2725 32.2734 25.4346 32.2734 16.9997C32.2734 8.56485 25.4356 1.72701 17.0007 1.72701Z"
                                                fill="#00816B"
                                            />
                                            <path
                                                d="M26.1445 20.4282C27.7809 20.4282 28.8111 19.3133 28.8111 17.6769C28.8111 16.0405 27.7809 14.7139 26.1445 14.7139V17.7615V20.4282Z"
                                                fill="#A3CDC3"
                                            />
                                            <path
                                                d="M8.23828 14.7134C6.60188 14.7134 5.57168 15.8283 5.57168 17.4647C5.57168 19.1011 6.60188 20.4277 8.23828 20.4277V17.3801V14.7134Z"
                                                fill="#A3CDC3"
                                            />
                                            <path
                                                d="M7.77344 12.9287C7.77344 11.8576 8.64173 10.9893 9.71283 10.9893H24.8817C25.9528 10.9893 26.8211 11.8576 26.8211 12.9287V22.7641C26.8211 23.8352 25.9528 24.7035 24.8817 24.7035H9.71283C8.64173 24.7035 7.77344 23.8352 7.77344 22.7641V12.9287Z"
                                                fill="#00816B"
                                            />
                                            <path d="M16.2383 8.61914H18.524V11.6668H16.2383V8.61914Z" fill="#00816B" />
                                            <path
                                                d="M9.38281 14.3671C9.38281 13.296 10.2511 12.4277 11.3222 12.4277H23.4434C24.5145 12.4277 25.3828 13.296 25.3828 14.3671V21.155C25.3828 22.2261 24.5145 23.0944 23.4434 23.0944H11.3222C10.2511 23.0944 9.38281 22.2261 9.38281 21.155V14.3671Z"
                                                fill="white"
                                            />
                                            <path
                                                d="M20.4715 18.7778C20.7988 18.7778 21.0641 18.3798 21.0641 17.8889C21.0641 17.398 20.7988 17 20.4715 17C20.1442 17 19.8789 17.398 19.8789 17.8889C19.8789 18.3798 20.1442 18.7778 20.4715 18.7778Z"
                                                fill="#00816B"
                                            />
                                            <path
                                                d="M15.7305 19.666C16.2569 20.017 16.8755 20.2043 17.5082 20.2043C18.141 20.2043 18.7596 20.017 19.286 19.666"
                                                fill="#00816B"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M15.4283 19.4647C15.5397 19.2976 15.7655 19.2524 15.9326 19.3639C16.3993 19.675 16.9477 19.8411 17.5086 19.8411C18.0696 19.8411 18.618 19.675 19.0847 19.3639C19.2518 19.2524 19.4776 19.2976 19.589 19.4647C19.7004 19.6318 19.6552 19.8576 19.4881 19.969C18.9019 20.3598 18.2132 20.5684 17.5086 20.5684C16.8041 20.5684 16.1153 20.3598 15.5291 19.969C15.362 19.8576 15.3169 19.6318 15.4283 19.4647Z"
                                                fill="#00816B"
                                            />
                                            <path
                                                d="M14.5457 18.7778C14.873 18.7778 15.1383 18.3798 15.1383 17.8889C15.1383 17.398 14.873 17 14.5457 17C14.2184 17 13.9531 17.398 13.9531 17.8889C13.9531 18.3798 14.2184 18.7778 14.5457 18.7778Z"
                                                fill="#00816B"
                                            />
                                            <path
                                                d="M20.0482 7.85682C20.0482 8.69839 18.8543 9.38063 17.3815 9.38063C15.9088 9.38063 14.7148 8.69839 14.7148 7.85682C14.7148 7.01524 15.9088 6.33301 17.3815 6.33301C18.8543 6.33301 20.0482 7.01524 20.0482 7.85682Z"
                                                fill="#A3CDC3"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className={s.p3}>
                                    *Your prospects or patients will see this message in an email.
                                </div>
                            </div>
                        </div>
                    </div>
                    {!shouldShowKlarityUi && (
                        <>
                            <AppointmentLinkEdit />
                            <NotificationEmailEdit />
                        </>
                    )}
                </div>
                <GreetingSetUpModal
                    show={showGreetingSetUpModal}
                    onCannel={() => {
                        setShowGreetingSetUpModal(false);
                    }}
                    onSuccess={() => {
                        fetchInfo();
                        setShowGreetingSetUpModal(false);
                    }}
                />
            </Spin>
            <div className={commonS.chatWrap}>
                <AIChat pageId={EAIChatPageId.PROVIDER_MESSAGE_SETTING} defaultShow={false} makeFix={false} />
            </div>
        </div>
    );
};

export default AvaSettingPage;
